'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'

import { registerUser } from '@/actions/user-auth'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { registerFormSchema, type RegisterFormValues } from '@/lib/auth/schemas'

export function RegisterForm() {
  const router = useRouter()
  const [serverError, setServerError] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  function onSubmit(values: RegisterFormValues) {
    setServerError(null)
    const formData = new FormData()
    formData.set('firstName', values.firstName)
    formData.set('lastName', values.lastName)
    formData.set('username', values.username)
    formData.set('email', values.email)
    formData.set('password', values.password)
    formData.set('confirmPassword', values.confirmPassword)
    startTransition(async () => {
      const result = await registerUser(formData)
      if (result.ok) {
        // Posle registracije vodimo korisnika kroz prilagođavanje platforme.
        router.push('/prilagodjavanje')
        router.refresh()
      } else {
        setServerError(result.message ?? 'Greška pri registraciji')
      }
    })
  }

  const disabled = isSubmitting || isPending

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="firstName">Ime</Label>
        <input
          id="firstName"
          type="text"
          autoComplete="given-name"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.firstName)}
          aria-describedby={errors.firstName ? 'firstName-error' : undefined}
          {...register('firstName')}
        />
        {errors.firstName && (
          <p id="firstName-error" className="text-destructive text-xs">
            {errors.firstName.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lastName">Prezime</Label>
        <input
          id="lastName"
          type="text"
          autoComplete="family-name"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.lastName)}
          aria-describedby={errors.lastName ? 'lastName-error' : undefined}
          {...register('lastName')}
        />
        {errors.lastName && (
          <p id="lastName-error" className="text-destructive text-xs">
            {errors.lastName.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="username">Korisničko ime</Label>
        <input
          id="username"
          type="text"
          autoComplete="username"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.username)}
          aria-describedby={errors.username ? 'username-error' : undefined}
          {...register('username')}
        />
        {errors.username && (
          <p id="username-error" className="text-destructive text-xs">
            {errors.username.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="email">Email adresa</Label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? 'email-error' : undefined}
          {...register('email')}
        />
        {errors.email && (
          <p id="email-error" className="text-destructive text-xs">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="password">Lozinka</Label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.password)}
          aria-describedby={errors.password ? 'password-error' : undefined}
          {...register('password')}
        />
        {errors.password && (
          <p id="password-error" className="text-destructive text-xs">
            {errors.password.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="confirmPassword">Potvrdi lozinku</Label>
        <input
          id="confirmPassword"
          type="password"
          autoComplete="new-password"
          className="border-input bg-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          aria-invalid={Boolean(errors.confirmPassword)}
          aria-describedby={errors.confirmPassword ? 'confirm-error' : undefined}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p id="confirm-error" className="text-destructive text-xs">
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      {serverError && (
        <div role="alert" className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
          {serverError}
        </div>
      )}

      <Button type="submit" disabled={disabled}>
        {disabled ? 'Registrujem...' : 'Registruj se'}
      </Button>

      <p className="text-muted-foreground text-sm">
        Koje podatke čuvamo i zašto piše u{' '}
        <Link href="/privatnost" className="text-primary underline underline-offset-4">
          politici privatnosti
        </Link>
        .
      </p>

      <p className="text-muted-foreground text-sm">
        Već imaš nalog?{' '}
        <Link href="/prijava" className="text-primary underline underline-offset-4">
          Prijavi se
        </Link>
      </p>
    </form>
  )
}
